// =============================================================================
// CC-DEV1-32 — VUE D'ENSEMBLE INSTALLATEUR (TEF, régie active)
// -----------------------------------------------------------------------------
// Besoin terrain : voir TOUS ses dossiers, répartis par statut (KPI = filtre),
// et ouvrir un dossier depuis la liste.
//   • Données : GET /api/metier/installateur/overview (filtré RBAC installateur).
//   • SOCLE réutilisable : pattern KPI-cliquable → filtre liste (par profil).
//   • SPÉCIFIQUE TEF : les 15 libellés/regroupements (amont OH / ANAH / fins).
//   • Buckets marqués "à fiabiliser" (reliable=false) : badge explicite, le
//     statut existe mais le moteur ne le mappe pas encore de façon fiable
//     (cycle financier ANAH avance/solde + classé sans suite — audit 42).
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Loader2, Search, ChevronRight, AlertTriangle, LayoutGrid } from "lucide-react";
import { API, getAuthHeaders, formatMontant } from "./dossier/helpers";

// Couleur sémantique par bucket (variables CSS --oh-stat-*, dual dark/light).
const BUCKET_COLOR = {
  cree: "blue", visite_planifiee: "blue", visite_effectuee: "cyan", montage: "purple",
  devis_attente: "amber",
  depose: "blue", instruction: "yellow", octroye: "green",
  demande_avance: "amber", avance_payee: "cyan", travaux: "orange",
  demande_solde: "amber", solde: "green",
  rejete: "red", abandonne: "red",
};

const GROUP_TITLES = {
  amont: "Phase amont (avant dépôt ANAH)",
  anah: "Phase ANAH (suivi subvention)",
  negatif: "Hors flux",
};
const GROUP_ORDER = ["amont", "anah", "negatif"];

function KpiCard({ bucket, active, onClick }) {
  const c = BUCKET_COLOR[bucket.key] || "blue";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`installateur-kpi-${bucket.key}`}
      aria-label={`Filtrer : ${bucket.label} (${bucket.count})`}
      aria-pressed={active}
      className="relative rounded-lg p-3 text-left transition-all hover:brightness-110"
      style={{
        background: active ? `var(--oh-stat-${c}-active-bg)` : `var(--oh-stat-${c}-bg)`,
        border: `1px solid ${active ? `var(--oh-stat-${c}-active-border)` : `var(--oh-stat-${c}-border)`}`,
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xl font-medium" style={{ color: `var(--oh-stat-${c}-text)` }}>
          {bucket.count}
        </span>
        {!bucket.reliable && (
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0.5 rounded"
            title="Statut affiché mais non fiabilisé par le moteur (cf. audit 42)"
            style={{ color: "var(--oh-stat-amber-text)", background: "var(--oh-stat-amber-bg)" }}
          >
            <AlertTriangle className="w-2.5 h-2.5" /> à fiabiliser
          </span>
        )}
      </div>
      <div className="text-[11px] mt-1 leading-tight" style={{ color: "var(--oh-text)" }}>
        {bucket.label}
      </div>
    </button>
  );
}

export default function InstallateurOverview({ tenantId, onOpenDossier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeBucket, setActiveBucket] = useState(null); // null = tous
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/metier/installateur/overview`, {
        headers: getAuthHeaders(tenantId),
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const toggleBucket = (key) => setActiveBucket((prev) => (prev === key ? null : key));

  const buckets = data?.buckets || [];
  const dossiers = data?.dossiers || [];
  const activeDef = buckets.find((b) => b.key === activeBucket);

  const filtered = dossiers.filter((d) => {
    if (activeBucket) {
      if (activeBucket === "devis_attente") { if (!d.devis_attente) return false; }
      else if (d.bucket !== activeBucket) return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${d.reference} ${d.client_nom} ${d.anah_reference}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 justify-center" data-testid="installateur-overview-loading">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
        <span style={{ color: "var(--oh-text-muted)" }}>Chargement de vos dossiers…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center" data-testid="installateur-overview-error">
        <p className="mb-3" style={{ color: "var(--oh-stat-red-text)" }}>Impossible de charger vos dossiers : {error}</p>
        <button onClick={load} className="text-sm underline" style={{ color: "var(--oh-text)" }}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-2 sm:p-4" data-testid="installateur-overview">
      {/* En-tête + TOTAL */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Mes dossiers</h2>
          <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
            Vue d'ensemble — cliquez un statut pour filtrer la liste.
          </p>
        </div>
        <button onClick={load} aria-label="Rafraîchir" data-testid="installateur-overview-refresh"
          className="p-2 rounded-lg" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
          <RefreshCw className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
        </button>
      </div>

      {/* Carte TOTAL (réinitialise le filtre) */}
      <button
        type="button"
        onClick={() => setActiveBucket(null)}
        data-testid="installateur-kpi-total"
        aria-pressed={activeBucket === null}
        className="w-full rounded-lg p-4 flex items-center justify-between transition-all hover:brightness-110"
        style={{
          background: activeBucket === null ? "var(--oh-stat-blue-active-bg)" : "var(--oh-bg-elevated)",
          border: `1px solid ${activeBucket === null ? "var(--oh-stat-blue-active-border)" : "var(--oh-border)"}`,
        }}
      >
        <span className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--oh-text)" }}>
          <LayoutGrid className="w-4 h-4" style={{ color: "var(--oh-stat-blue-text)" }} />
          Tous mes dossiers
        </span>
        <span className="text-2xl font-medium" style={{ color: "var(--oh-stat-blue-text)" }}>{data?.total ?? 0}</span>
      </button>

      {/* KPIs groupés */}
      {GROUP_ORDER.map((grp) => {
        const grpBuckets = buckets.filter((b) => b.group === grp);
        if (!grpBuckets.length) return null;
        return (
          <div key={grp} className="space-y-2">
            <p className="text-[10px] uppercase tracking-wide font-medium" style={{ color: "var(--oh-text-muted)" }}>
              {GROUP_TITLES[grp]}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {grpBuckets.map((b) => (
                <KpiCard key={b.key} bucket={b} active={activeBucket === b.key} onClick={() => toggleBucket(b.key)} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Recherche */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--oh-text-muted)" }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (réf., client, réf. ANAH)…"
          data-testid="installateur-overview-search"
          className="w-full pl-9 pr-3 py-2 rounded-lg text-sm"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
        />
      </div>

      {/* Liste filtrable */}
      <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--oh-border)" }} data-testid="installateur-overview-list">
        <div className="px-3 py-2 text-xs flex items-center justify-between"
          style={{ background: "var(--oh-bg-elevated)", borderBottom: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}>
          <span>{activeDef ? `Filtre : ${activeDef.label}` : "Tous les dossiers"} — {filtered.length} dossier(s)</span>
          {activeBucket && (
            <button onClick={() => setActiveBucket(null)} className="underline" style={{ color: "var(--oh-text)" }}>
              Effacer le filtre
            </button>
          )}
        </div>
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucun dossier.</div>
        ) : (
          <ul>
            {filtered.map((d) => {
              const c = BUCKET_COLOR[d.bucket] || "blue";
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => onOpenDossier?.(d.id)}
                    data-testid={`installateur-row-${d.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:brightness-110"
                    style={{ borderBottom: "1px solid var(--oh-border)", background: "var(--oh-bg)" }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--oh-text)", fontFamily: "'IBM Plex Mono', monospace" }}>
                        {d.reference || "—"}
                      </p>
                      <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>{d.client_nom}</p>
                    </div>
                    {d.montant_devis ? (
                      <span className="text-xs hidden sm:inline" style={{ color: "var(--oh-text-muted)" }}>{formatMontant(d.montant_devis)}</span>
                    ) : null}
                    <span
                      className="text-[10px] px-2 py-1 rounded whitespace-nowrap"
                      style={{ color: `var(--oh-stat-${c}-text)`, background: `var(--oh-stat-${c}-bg)`, border: `1px solid var(--oh-stat-${c}-border)` }}
                    >
                      {d.statut_label}
                    </span>
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "var(--oh-text-muted)" }} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
